'use client';
import {
  createUpgradeTemplate,
  useGetAccountTeam,
  useListSubscriptions,
} from 'api/frontend';
import {
  AddonInstance,
  Cluster,
  ResolvedEntitlementFeaturesItem,
  UpgradeTemplate,
} from 'api/models';
import { ConsoleLoader } from 'components/Loader';
import { LockingWrapper } from 'components/LockingWrapper';
import Page from 'components/Page';
import { RouterLink } from 'components/RouterLink';
import BaseTable from 'components/design-system/Table/BaseTable';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import useUserAccountState from 'hooks/useUserAccountState';
import { useSnackbar } from 'notistack';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebounce } from 'react-use';
import {
  Badge,
  Breadcrumbs,
  Button,
  IconWithBackground,
  InfoTooltip,
  RadioGroup,
  Select,
  StepBase,
  Tooltip,
} from 'subframe/index';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { Searchbar } from 'components/design-system/Searchbar';
import { useAddonIdToNameMap } from 'hooks/useAddonIdToNameMap';
import {
  useListAddonInstancesInfinite,
  useListUpgradeTemplatesInfinite,
} from 'api/frontend-infinite';
import { flattenPages } from 'utils/arrays';
import { Stack } from 'components/utils';
import { useListClustersInfinite } from 'api/frontend-infinite';
import { clusterFlattenPages } from 'utils/arrays';
import { AtomicTooltip } from 'components/design-system';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import semver from 'semver';
import { useSearchParams } from 'react-router-dom';
import { useAvailableK8sVersionsForUpgrade } from 'hooks/useAvailableK8sVersionsForUpgrade';

function AddonUpgradeTemplatesRequestTemplate() {
  const { logEvent } = AnalyticsEventLogger();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { account, currentOrganization } = useUserAccountState();
  const addonUpgradeTemplatesBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/templates/addons',
  );
  const clustersBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/clusters',
  );
  //Subscriptions
  const { data: subscriptions } = useListSubscriptions(
    currentOrganization?.slug,
    {},
    {
      request: { headers: { Authorization: `Bearer ${account?.token}` } },
    },
  );
  const showTemplateLock = subscriptions
    ? !subscriptions?.total_entitlement?.features?.includes(
        'request_upgrade_template',
      ) &&
      !subscriptions?.total_entitlement?.features?.includes(
        'request_addon_upgrade_template',
      )
    : true;

  const { mutate } = useListUpgradeTemplatesInfinite(
    {},
    {
      request: {
        headers: { Authorization: `Bearer ${account.token}` },
      },
    },
  );

  // Clusters
  const { data: clustersList, isLoading: isLoadingClustersList } =
    useListClustersInfinite(
      '',
      { status: 'active' },
      {
        request: { headers: { Authorization: `Bearer ${account?.token}` } },
      },
      {
        initialSize: 100,
        revalidateFirstPage: false,
      },
    );

  const clusterData = clusterFlattenPages(clustersList || []) || [];

  const realClusters = clusterData
    ? clusterData.filter((c) => c.internal_k8s_ref !== 'example-cluster-ref')
    : [];

  //  Add-ons
  const addonIdToNameMap = useAddonIdToNameMap({ token: account.token });
  const { data: addonInstancesData, isLoading: addonInstancesLoading } =
    useListAddonInstancesInfinite(
      {
        filter: ['status:active'],
      },
      {
        request: { headers: { Authorization: `Bearer ${account.token}` } },
      },
    );
  const addonInstances = flattenPages(addonInstancesData || []);
  const clusterIdToDetailsMap: Map<string, Cluster> = new Map(
    realClusters.map((c) => [c.id, c]),
  );
  const displayData = (
    addonInstances && addonIdToNameMap.size
      ? addonInstances.map((instance) => ({
          ...instance,
          addon_name: addonIdToNameMap.get(instance.addon_id) || '',
          cluster_name:
            clusterIdToDetailsMap.get(instance.cluster_id)?.name || '',
          environment:
            clusterIdToDetailsMap.get(instance.cluster_id)?.environment || '',
          cluster_version:
            clusterIdToDetailsMap.get(instance.cluster_id)?.version || '',
          can_be_requested:
            (subscriptions?.total_entitlement?.addons || []).length > 0
              ? (
                  subscriptions?.total_entitlement?.addons?.filter(
                    (a) =>
                      a.addon_id === instance.addon_id &&
                      a.individual_upgrade_allowed,
                  ) || []
                ).length > 0
              : true,
        }))
      : []
  ).sort((a, b) => {
    if (a.can_be_requested == b.can_be_requested) {
      return a.addon_name.localeCompare(b.addon_name);
    } else if (a.can_be_requested) {
      return -1;
    } else {
      return 1;
    }
  });

  const [selectedAddonInstance, setSelectedAddonInstance] = useState<
    | (AddonInstance & {
        addon_name: string;
        cluster_name: string;
        environment: string;
        cluster_version: string;
        can_be_requested: boolean;
      })
    | undefined
  >(undefined);
  useEffect(() => {
    if (!searchParams.has('addon_id') || !searchParams.has('cluster_id')) {
      return;
    }
    const addonId = searchParams.get('addon_id');
    const clusterId = searchParams.get('cluster_id');
    const foundInstance = displayData?.find(
      (v) => v.addon_id === addonId && v.cluster_id === clusterId,
    );
    if (foundInstance) {
      setSelectedAddonInstance(foundInstance);
    }
  }, [addonInstancesData, addonIdToNameMap]);

  const [searchAddon, setSearchAddon] = useState('');
  const [addonsToShow, setAddonsToShow] = useState(displayData);
  const handleOnChange = (
    _event: SyntheticEvent<Element, Event>,
    newValue: string,
  ) => {
    setSearchAddon(newValue);
  };

  useDebounce(
    () => {
      if (searchAddon) {
        logEvent('search-addon-to-upgrade', { searchAddon });
      }
    },
    1000,
    [searchAddon],
  );

  useEffect(() => {
    // handle search
    if (displayData.length > 0) {
      if (searchAddon !== '') {
        const found = displayData?.filter((item) =>
          item?.addon_name.toLowerCase().includes(searchAddon.toLowerCase()),
        );
        setAddonsToShow(found);
      } else {
        setAddonsToShow(displayData);
      }
    }
  }, [addonInstancesData, addonIdToNameMap, clustersList, searchAddon]);

  const { data: team } = useGetAccountTeam('default', 'default', {
    request: { headers: { Authorization: `Bearer ${account.token}` } },
  });
  const realUsers = team?.members?.filter((member) => member.user_email) || [];

  const FormSchema = Yup.object().shape({
    nextAddonVersion: Yup.string().test(
      'is-semver',
      'Must be a valid semantic version',
      (value) => !value || semver.valid(semver.coerce(value)?.version) !== null,
    ),
  });

  const defaultValues = {
    nextAddonVersion: '',
  };

  const methods = useForm({
    resolver: yupResolver(FormSchema),
    defaultValues: defaultValues,
  });

  const {
    formState: { isValid },
  } = methods;

  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [targetK8sVersion, setTargetK8sVersion] = useState<string | undefined>(
    undefined,
  );

  const selectedCluster = clusterIdToDetailsMap.get(
    selectedAddonInstance?.cluster_id || '',
  );

  const availableK8sVersions = useAvailableK8sVersionsForUpgrade(
    selectedCluster,
    {
      filterMode: 'next-minor-only',
    },
  );

  const isTableLoading =
    addonInstancesLoading || isLoadingClustersList || !addonIdToNameMap?.size;
  const isClusterOboarded = realClusters?.length;

  const [requestTemplateLoading, setRequestTemplateLoading] = useState(false);
  const [selectedToVersion, setSelectedToVersion] = useState<string>();

  const requestUpgradeTemplate = async function () {
    if (requestTemplateLoading) {
      return;
    }
    // Re-enable when versions are enabled
    // if (!methods.formState.isValid) {
    //   return;
    // }

    setRequestTemplateLoading(true);
    logEvent('upgrades-templates-strategy-selected', {});

    if (!selectedAddonInstance?.id) {
      enqueueSnackbar(
        'Select an Project instance to create an Upgrade Template',
        {
          variant: 'error',
          autoHideDuration: toastAutoHideDuration,
        },
      );
      setRequestTemplateLoading(false);
      return;
    }

    if (!targetK8sVersion) {
      enqueueSnackbar(
        'Select Target Kubernetes Version for the Project Upgrade',
        {
          variant: 'error',
          autoHideDuration: toastAutoHideDuration,
        },
      );
      setRequestTemplateLoading(false);
      return;
    }
    try {
      const createdUpgradeTemplate: UpgradeTemplate =
        await createUpgradeTemplate(
          {
            environment: selectedAddonInstance.environment,
            resources: {
              addon_instances: [{ id: selectedAddonInstance.id }],
            },
            desired_version: selectedToVersion,
            owner: selectedOwner ? { id: selectedOwner } : undefined,
            target_kubernetes_version: targetK8sVersion || undefined,
          },
          { headers: { Authorization: `Bearer ${account.token}` } },
        );
      logEvent('addon-upgrade-template-created', {
        clusterId: selectedAddonInstance.cluster_id,
        templateId: createdUpgradeTemplate?.id,
        upgradeTemplateName: createdUpgradeTemplate?.name,
        ownerId: selectedOwner,
        targetK8sVersion: targetK8sVersion,
      });
      await mutate();
      enqueueSnackbar(
        "Upgrade Template request successful. We'll notify you once it is ready. Note that this process may take up to 2 days",
        {
          variant: 'success',
          autoHideDuration: toastAutoHideDuration,
        },
      );
      setRequestTemplateLoading(false);
      navigate(addonUpgradeTemplatesBasePath);
    } catch (err) {
      enqueueSnackbar(
        somethingWentWrong.replace(
          '<action>',
          'requesting this Upgrade Template',
        ),
        {
          variant: 'error',
          autoHideDuration: toastAutoHideDuration,
        },
      );
      setRequestTemplateLoading(false);
    }
  };

  useEffect(() => {
    setTargetK8sVersion(undefined);
  }, [selectedAddonInstance?.id]);

  return (
    <Page title="Project Upgrade Templates">
      <div className="flex h-full flex-col items-start gap-6 bg-default-background px-3 pt-8 ml-8 w-[calc(100% - 32px]">
        <Breadcrumbs>
          <Breadcrumbs.Item>Upgrade Copilot</Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item>Upgrade Templates</Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <RouterLink to={addonUpgradeTemplatesBasePath}>
            <Breadcrumbs.Item>Projects</Breadcrumbs.Item>
          </RouterLink>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item active={true}>Request Template</Breadcrumbs.Item>
        </Breadcrumbs>
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex w-full items-center justify-end gap-2">
            <div className="flex grow shrink-0 basis-0 flex-col items-start justify-end gap-2">
              <span className="text-section-header font-section-header text-default-font">
                Request Project Upgrade Template
              </span>
              <span className="text-body font-body text-subtext-color">
                Select a representative Project Instance to get an Upgrade
                Template
              </span>
            </div>
          </div>
        </div>
        <StepBase
          stepTitle="Select a Representative Project Instance"
          stepTitleTooltip={
            <Tooltip>
              An Upgrade Template can be used to instantiate Upgrade Plans for
              matching Project instances across fleet.
            </Tooltip>
          }
          stepNumber="1"
          defaultOpen={true}
        >
          {isTableLoading ? (
            <ConsoleLoader />
          ) : (
            <>
              {displayData?.length ? (
                <div className="flex flex-col w-full items-end">
                  <Searchbar onChange={handleOnChange} />
                </div>
              ) : (
                <></>
              )}

              {isClusterOboarded ? (
                <BaseTable
                  settings={{
                    localStorageKey: 'create_addon_upgrade_templates_table',
                    dataCyPrefix: 'create-addon-upgrade-template',
                  }}
                  data={addonsToShow}
                  noMatchingData={Boolean(
                    displayData.length && !addonsToShow.length,
                  )}
                  onRowClick={(addonInstance) => {
                    if (addonInstance.can_be_requested) {
                      setSelectedAddonInstance(addonInstance);
                    }
                  }}
                  columns={[
                    {
                      id: 'addon',
                      title: 'Project',
                      titleStyle: { width: '40%' },
                      cellType: 'cell',
                      render: (addonInstance) => {
                        return (
                          <>
                            <RadioGroup>
                              <AtomicTooltip
                                tooltipContent={
                                  addonInstance.can_be_requested
                                    ? addonInstance.addon_name
                                    : 'This Project is not included in your subscription. Upgrade to Business Plan to enable it.'
                                }
                              >
                                <RadioGroup.Option
                                  checked={
                                    selectedAddonInstance?.id ===
                                    addonInstance.id
                                  }
                                  disabled={
                                    !addonInstance.can_be_requested ||
                                    requestTemplateLoading
                                  }
                                  label={addonInstance.addon_name}
                                  value={addonInstance.id}
                                >
                                  {!addonInstance.can_be_requested && (
                                    <IconWithBackground icon="FeatherLock" />
                                  )}
                                </RadioGroup.Option>
                              </AtomicTooltip>
                            </RadioGroup>
                          </>
                        );
                      },
                    },
                    {
                      id: 'tag',
                      title: (
                        <Stack direction="row" spacing={1}>
                          Tag
                          <InfoTooltip tooltipText="The Tags that have been locally applied to Project container images" />
                        </Stack>
                      ),
                      titleStyle: { width: '30%' },
                      cellType: 'cell',
                      render: (addonInstance) => {
                        return (
                          <span className="whitespace-nowrap text-body font-body text-subtext-color">
                            {addonInstance.primary_component?.image?.tag}
                          </span>
                        );
                      },
                    },
                    {
                      id: 'cluster',
                      title: 'Cluster',
                      titleStyle: { width: '30%' },
                      cellType: 'cell',
                      render: (addonInstance) => {
                        return (
                          <span className="text-body font-body text-subtext-color">
                            {addonInstance.cluster_name}
                          </span>
                        );
                      },
                    },
                  ]}
                  actions={[]}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-body font-body text-default-font">
                    No clusters onboarded so far. Click here to onboard your
                    first cluster
                  </span>
                  <RouterLink to={`${clustersBasePath}/new`}>
                    <Button
                      size="small"
                      icon="FeatherPlus"
                      onClick={() =>
                        logEvent(
                          'upgrades-templates-create-onboard-cluster-btn-click',
                        )
                      }
                    >
                      Add Cluster
                    </Button>
                  </RouterLink>
                </div>
              )}
            </>
          )}
        </StepBase>
        {/*
          Bring in once it fully works
        <StepBase
          stepTitle="Select Project Version"
          stepTitleTooltip=""
          stepNumber="2"
          badges={<Badge variant="neutral">Optional</Badge>}
          defaultOpen={true}
        >
          <FormProvider methods={methods}>
            <TextField
              error={!isValid}
              helpText={!isValid ? 'Must be a valid semantic version' : ''}
              className="w-36"
            >
              <TextField.Input
                {...methods.register('nextAddonVersion')}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setSelectedToVersion(value);
                }}
                disabled={requestTemplateLoading}
                placeholder="Next version"
              />
            </TextField>
          </FormProvider>
        </StepBase>
        */}
        <StepBase
          stepTitle="Select Owner"
          stepTitleTooltip={
            <Tooltip>
              Select an Owner for this Upgrade Template. Once assigned, only the
              Owner can Approve the Upgrade Template. The Owner can be changed
              at any time.
            </Tooltip>
          }
          stepNumber="2"
          badges={<Badge variant="neutral">Optional</Badge>}
          defaultOpen={true}
        >
          <div className="flex w-full flex-wrap items-center gap-6">
            <Select
              placeholder="Change Owner"
              onValueChange={(value) => setSelectedOwner(value)}
              disabled={requestTemplateLoading}
            >
              <Select.Item value="">Owner Not Required</Select.Item>
              {realUsers.map((user, idx) => (
                <Select.Item value={user.user_id || ''} key={idx}>
                  {user.user_name || ''}
                </Select.Item>
              ))}
            </Select>
          </div>
        </StepBase>

        <StepBase
          stepTitle="Select Target K8s Version"
          stepTitleTooltip={
            <Tooltip>
              Select the Kubernetes version that the project will run on after
              the upgrade.
            </Tooltip>
          }
          stepNumber="3"
          badges={<Badge variant="warning">Required</Badge>}
          defaultOpen={true}
        >
          <div className="flex w-full items-center">
            <Select
              label=""
              placeholder="Select K8s version"
              helpText=""
              value={undefined}
              onValueChange={(value: string) => {
                setTargetK8sVersion(value);
              }}
              disabled={!selectedAddonInstance?.id}
            >
              {availableK8sVersions.map((version) => (
                <Select.Item key={version} value={version}>
                  {version}
                </Select.Item>
              ))}
            </Select>
          </div>
        </StepBase>
        <LockingWrapper
          feature={ResolvedEntitlementFeaturesItem.request_upgrade_template}
          tooltipText={
            displayData?.length
              ? undefined //default, handled in LockingWrapper
              : 'Add a cluster to get Upgrade Template'
          }
          showLock={showTemplateLock}
          defaultAction={() => requestUpgradeTemplate()}
        >
          <div className="flex w-full items-center gap-2">
            <Button
              size="large"
              icon="FeatherPlus"
              loading={requestTemplateLoading}
              disabled={selectedAddonInstance?.id === undefined}
            >
              Request Upgrade Template
            </Button>
          </div>
        </LockingWrapper>
      </div>
    </Page>
  );
}
export default AddonUpgradeTemplatesRequestTemplate;
